import { z } from "zod";

// ---------------------------------------------------------------------------
// CRM user provisioning (GRA-56)
//
// A sales team member is a hand-typed roster row. Every CRM-derived number
// about a person used to depend on that row's work email happening to equal
// the owner email in the CRM; when it did not, values were attributed to
// nobody and nothing said so. A CRM identity makes the link explicit: an admin
// decides once which CRM user each roster member is, and gathers attribute by
// the CRM user id from then on. Email matching is only ever a suggestion.
// ---------------------------------------------------------------------------

/** The Upsales MCP toolkit — the canonical key for both Upsales slugs. */
const UPSALES_MCP_SLUG = "CUSTOM_UPSALES_MCP";
/** The vendor Upsales toolkit, the same CRM behind a different auth config. */
const UPSALES_VENDOR_SLUG = "CUSTOM_UPSALES";

/**
 * The key a CRM identity is stored under for a toolkit slug.
 *
 * Uppercased, and the two Upsales toolkits collapse to one: they read the same
 * CRM with the same user ids, so a seller mapped once must stay mapped when a
 * business moves from one auth config to the other.
 */
export function canonicalCrmToolkitSlug(slug: string): string {
  const upper = slug.trim().toUpperCase();
  return upper === UPSALES_VENDOR_SLUG ? UPSALES_MCP_SLUG : upper;
}

/**
 * How an identity came to be.
 *
 * - `import`     — the member was created from the CRM user list.
 * - `manual`     — an admin picked the CRM user.
 * - `auto-email` — an exact email match an admin has not reviewed yet. Used
 *                  for attribution like the others, but shown as a
 *                  suggestion in the mapping screen until someone saves it.
 */
export const CRM_IDENTITY_SOURCES = ["import", "manual", "auto-email"] as const;
export const CrmIdentitySource = z.enum(CRM_IDENTITY_SOURCES);
export type CrmIdentitySource = z.infer<typeof CrmIdentitySource>;

/** Which CRM user a roster member is, for one toolkit. */
export const CrmIdentity = z.object({
  /** Canonical toolkit slug, see {@link canonicalCrmToolkitSlug}. */
  toolkitSlug: z.string().min(1),
  /** The CRM's own user id: Upsales `user.id`, HubSpot owner `id`. */
  externalUserId: z.string().min(1),
  externalEmail: z.string().nullable().default(null),
  displayName: z.string().default(""),
  /** CRM role/team, informational only. */
  roleName: z.string().nullable().default(null),
  mappedByUid: z.string().nullable().default(null),
  mappedAt: z.string().nullable().default(null),
  source: CrmIdentitySource,
});
export type CrmIdentity = z.infer<typeof CrmIdentity>;

/** One user in a CRM's user list, normalised across CRMs. */
export const CrmUser = z.object({
  externalUserId: z.string().min(1),
  email: z.string().nullable().default(null),
  name: z.string().default(""),
  roleName: z.string().nullable().default(null),
  active: z.boolean().default(true),
});
export type CrmUser = z.infer<typeof CrmUser>;

/**
 * The last fetched user list for one CRM, stored at
 * `Businesses/{businessId}/crmUsers/{toolkitSlug}` so neither the mapping
 * screen nor the nightly job has to hit the CRM for the roster every time.
 */
export interface CrmUsersSnapshot {
  toolkitSlug: string;
  fetchedAt: string;
  users: CrmUser[];
}

/** The identity a member has for a toolkit, or null. */
export function crmIdentityFor(
  member: { crmIdentities?: readonly CrmIdentity[] | null },
  toolkitSlug: string,
): CrmIdentity | null {
  const key = canonicalCrmToolkitSlug(toolkitSlug);
  return (member.crmIdentities ?? []).find((i) => i.toolkitSlug === key) ?? null;
}

/**
 * A member's identities with the one for `toolkitSlug` replaced — or removed,
 * when `identity` is null. One identity per toolkit per member.
 */
export function withCrmIdentity(
  identities: readonly CrmIdentity[] | null | undefined,
  toolkitSlug: string,
  identity: CrmIdentity | null,
): CrmIdentity[] {
  const key = canonicalCrmToolkitSlug(toolkitSlug);
  const rest = (identities ?? []).filter((i) => i.toolkitSlug !== key);
  return identity ? [...rest, { ...identity, toolkitSlug: key }] : rest;
}

function normEmail(email: string | null | undefined): string | null {
  const e = email?.trim().toLowerCase();
  return e ? e : null;
}

export interface CrmMatchCandidateMember {
  id: string;
  email: string | null;
  crmIdentities?: readonly CrmIdentity[] | null;
}

/**
 * Exact, case-insensitive email matches between roster members without an
 * identity for this toolkit and CRM users nobody is mapped to yet.
 *
 * Only unambiguous pairs: an email shared by two members, or by two CRM users,
 * suggests nothing. A wrong suggestion that an admin saves without looking is
 * worse than no suggestion, so there is no name matching here either.
 */
export function suggestCrmMatches(
  members: readonly CrmMatchCandidateMember[],
  users: readonly CrmUser[],
  toolkitSlug: string,
): Map<string, CrmUser> {
  const mappedUserIds = new Set(
    members
      .map((m) => crmIdentityFor(m, toolkitSlug)?.externalUserId)
      .filter((id): id is string => id != null),
  );

  const usersByEmail = new Map<string, CrmUser[]>();
  for (const u of users) {
    const email = normEmail(u.email);
    if (email == null || mappedUserIds.has(u.externalUserId)) continue;
    usersByEmail.set(email, [...(usersByEmail.get(email) ?? []), u]);
  }
  const memberCountByEmail = new Map<string, number>();
  for (const m of members) {
    const email = normEmail(m.email);
    if (email) memberCountByEmail.set(email, (memberCountByEmail.get(email) ?? 0) + 1);
  }

  const out = new Map<string, CrmUser>();
  for (const m of members) {
    if (crmIdentityFor(m, toolkitSlug) != null) continue;
    const email = normEmail(m.email);
    if (email == null || memberCountByEmail.get(email) !== 1) continue;
    const candidates = usersByEmail.get(email) ?? [];
    if (candidates.length === 1) out.set(m.id, candidates[0]!);
  }
  return out;
}

function normName(name: string | null | undefined): string {
  return (name ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** "Charlotte" matches "Charlotte Abrahamsen"; "Jonas Larsson" matches itself. */
function namesMatch(memberName: string, userName: string): boolean {
  const member = normName(memberName);
  const user = normName(userName);
  if (member.length === 0 || user.length === 0) return false;
  if (member === user) return true;
  // A roster entry typed as a first name only, against the CRM's full name.
  return !member.includes(" ") && user.split(" ")[0] === member;
}

export interface CrmCandidateMember extends CrmMatchCandidateMember {
  name: string;
}

/**
 * Unlinked roster members an import would most likely duplicate: same name
 * as the CRM user (or the roster has just the first name), but an email that
 * did not match exactly, so {@link suggestCrmMatches} left them alone.
 *
 * Not a link and never applied on its own. It is what the import dialog
 * asks the admin about: link this CRM user to the existing member, or create
 * a new one. Only unambiguous pairs, like the email suggestions.
 */
export function findPossibleDuplicates(
  members: readonly CrmCandidateMember[],
  users: readonly CrmUser[],
  toolkitSlug: string,
): Map<string, CrmCandidateMember> {
  const emailSuggestions = suggestCrmMatches(members, users, toolkitSlug);
  const suggestedUserIds = new Set(
    [...emailSuggestions.values()].map((u) => u.externalUserId),
  );
  const unlinked = members.filter(
    (m) => crmIdentityFor(m, toolkitSlug) == null && !emailSuggestions.has(m.id),
  );
  const linkedUserIds = new Set(
    members
      .map((m) => crmIdentityFor(m, toolkitSlug)?.externalUserId)
      .filter((id): id is string => id != null),
  );
  const candidates = users.filter(
    (u) =>
      u.active &&
      !suggestedUserIds.has(u.externalUserId) &&
      !linkedUserIds.has(u.externalUserId),
  );

  const out = new Map<string, CrmCandidateMember>();
  for (const user of candidates) {
    const matches = unlinked.filter((m) => namesMatch(m.name, user.name));
    if (matches.length !== 1) continue;
    const member = matches[0]!;
    // The member must not also fit another CRM user equally well.
    const rivals = candidates.filter((u) => namesMatch(member.name, u.name));
    if (rivals.length === 1) out.set(user.externalUserId, member);
  }
  return out;
}

export interface CrmMappingDrift {
  /** Active CRM users no roster member is mapped to. */
  unmappedUsers: CrmUser[];
  /** Members mapped to a CRM user that is inactive or gone from the CRM. */
  inactiveMappedMemberIds: string[];
}

/**
 * What an admin should look at after a CRM user list refresh. Pure — the
 * Apps page card and the team settings page both derive their counts from it.
 */
export function crmMappingDrift(
  members: readonly CrmMatchCandidateMember[],
  users: readonly CrmUser[],
  toolkitSlug: string,
): CrmMappingDrift {
  const userById = new Map(users.map((u) => [u.externalUserId, u]));
  const mapped = new Set<string>();
  const inactiveMappedMemberIds: string[] = [];
  for (const m of members) {
    const identity = crmIdentityFor(m, toolkitSlug);
    if (identity == null) continue;
    mapped.add(identity.externalUserId);
    const user = userById.get(identity.externalUserId);
    if (user == null || !user.active) inactiveMappedMemberIds.push(m.id);
  }
  return {
    unmappedUsers: users.filter((u) => u.active && !mapped.has(u.externalUserId)),
    inactiveMappedMemberIds,
  };
}

// ---------------------------------------------------------------------------
// tRPC inputs
// ---------------------------------------------------------------------------

export const GetCrmMappingInput = z.object({
  businessId: z.string().min(1),
});
export type GetCrmMappingInput = z.infer<typeof GetCrmMappingInput>;

export const RefreshCrmUsersInput = z.object({
  businessId: z.string().min(1),
  toolkitSlug: z.string().min(1),
});
export type RefreshCrmUsersInput = z.infer<typeof RefreshCrmUsersInput>;

export const SaveCrmMappingInput = z.object({
  businessId: z.string().min(1),
  toolkitSlug: z.string().min(1),
  links: z
    .array(
      z.object({
        memberId: z.string().min(1),
        /** Null unlinks the member from this CRM. */
        externalUserId: z.string().min(1).nullable(),
        /**
         * `auto-email` when the admin kept a suggestion as it was; anything
         * they picked themselves is `manual`.
         */
        source: z.enum(["manual", "auto-email"]).default("manual"),
      }),
    )
    .min(1)
    .max(500),
});
export type SaveCrmMappingInput = z.infer<typeof SaveCrmMappingInput>;

export const ImportCrmUsersInput = z.object({
  businessId: z.string().min(1),
  toolkitSlug: z.string().min(1),
  externalUserIds: z.array(z.string().min(1)).min(1).max(500),
  /**
   * Where new members go: one existing team, or one team per CRM role
   * (created when no team with that name exists; users without a role land
   * without a team).
   */
  /**
   * CRM user id → existing member id: the admin chose to link these users to
   * members they already have instead of creating new ones (possible
   * duplicates the dialog asked about).
   */
  linkTo: z.record(z.string().min(1), z.string().min(1)).default({}),
  target: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("team"), salesTeamId: z.string().min(1) }),
    z.object({ kind: z.literal("per-role") }),
  ]),
});
export type ImportCrmUsersInput = z.infer<typeof ImportCrmUsersInput>;
